import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getRoleName, useAuth } from '../context/AuthContext';
import { apiFetch, BASE_URL, TokenStorage } from '../services/api';
import '../styles/ChoosePlan.scss';


const API_URL = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';

const ROLES = [
  {
    key: 'individual',
    label: 'Researcher / Individual',
    desc: 'Check your manuscripts before journal submission.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#003c6c" strokeWidth="2">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
    planType: 'individual',
  },
  {
    key: 'institution',
    label: 'University / Institution',
    desc: 'Screen student and faculty submissions at scale.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#003c6c" strokeWidth="2">
        <path d="M3 10 12 4l9 6-9 6-9-6Z" />
        <path d="M5 12v6c0 1.5 3 3 7 3s7-1.5 7-3v-6" />
      </svg>
    ),
    planType: 'institution',
  },
  {
    key: 'publisher',
    label: 'Organisation / Publisher',
    desc: 'Automate manuscript screening for journals, publishers and corporate teams.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#003c6c" strokeWidth="2">
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    ),
    planType: 'company',
  },
];

interface Plan {
  amount: number;
  plan_id: string | number;
  pricing_id: string | number;
  name: string;
  plan_type?: string; // 'common' | 'individual' | 'institution' | 'company'
  price: number;
  discounted_amount: number;
  gst_amount: number;
  total_amount: number;
  currency_symbol: string;
  billing_cycle: string;
  max_papers: number;
  max_users: number;
  ai_detection: boolean;
  full_report: boolean;
  rest_api: boolean;
  contact_sales: boolean;
  is_contact_sales: boolean;
  trial_days: number;
}

declare global {
  interface Window { Razorpay: any; }
}

function planRank(plan: Plan): number {
  return Number(plan.total_amount ?? plan.discounted_amount ?? plan.price ?? plan.amount ?? 0);
}
function formatAmount(n: number) {
  return `${Math.round(n)}`;
}
export default function ChoosePlan() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const upgradeOnly = Boolean((location.state as any)?.upgradeOnly);

  const initialRole = (() => {
    if (upgradeOnly) return null;
    const roleName = getRoleName(user?.role).toLowerCase();
    const mappedKey = roleName === 'institution' ? 'institution'
      : roleName === 'publisher' ? 'publisher'
        : roleName === 'researcher' ? 'individual'
          : null;
    return mappedKey ? ROLES.find(r => r.key === mappedKey) ?? null : null;
  })();

  const [step, setStep] = useState<'role' | 'plan'>(upgradeOnly ? 'plan' : (initialRole ? 'plan' : 'role'));
  const [selectedRole, setSelectedRole] = useState<typeof ROLES[0] | null>(initialRole);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currency, setCurrency] = useState<{ symbol: string; code: string; show_gst: boolean, currency_id: number; } | null>(null);
  const [loading, setLoading] = useState(upgradeOnly);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [payingPlanId, setPayingPlanId] = useState<string | number | null>(null);

  // Current subscription, only fetched/used in upgradeOnly mode so we know
  // which plans count as an "upgrade".
  const [currentSub, setCurrentSub] = useState<any>(null);
  const [detectingRole, setDetectingRole] = useState(upgradeOnly);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchFilteredPlans = async (
    role: typeof ROLES[0],
    cycle: string,
    code: string,
    subForFilter: any | null
  ): Promise<{ currency: any; list: Plan[] }> => {
    const data = await fetch(`${API_URL}/plans/?type=${role.planType}&billing_cycle=${cycle}&currency=${code}`)
      .then(r => r.json());

    let list: Plan[] = data.plans || [];
    if (subForFilter) {
      const currentRank = Number(subForFilter.amount ?? 0);
      list = list.filter(p =>
        String(p.plan_id) !== String(subForFilter.plan_id) && planRank(p) > currentRank
      );
    }
    return { currency: data.currency || null, list };
  };

  const loadPlansForRole = async (role: typeof ROLES[0], subForFilter: any | null) => {
    setLoading(true);
    try {
      const c = await fetch(`${API_URL}/plans/detect-currency/`).then(r => r.json()).catch(() => null);
      const code = c?.code === 'INR' ? 'INR' : 'USD';
      const cycleMap: Record<string, string> = { monthly: 'Monthly', annual: 'Annual', lifetime: 'Annual' };
      const primaryCycle = subForFilter ? (cycleMap[String(subForFilter.billing_cycle || '').toLowerCase()] || 'Annual') : 'Annual';
      const fallbackCycle = primaryCycle === 'Annual' ? 'Monthly' : 'Annual';

      let { currency, list } = await fetchFilteredPlans(role, primaryCycle, code, subForFilter);

      if (subForFilter && list.length === 0) {
        const fallback = await fetchFilteredPlans(role, fallbackCycle, code, subForFilter);
        if (fallback.list.length > 0) {
          currency = fallback.currency;
          list = fallback.list;
        }
      }

      setCurrency(currency);
      setPlans(list);
    } catch {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Upgrade-only bootstrap ──────────────────────────────────
  useEffect(() => {
    if (!upgradeOnly) return;
    let cancelled = false;

    (async () => {
      setDetectingRole(true);
      setLoading(true);
      try {
        const sub = await apiFetch('/payments/my-subscription/');
        if (cancelled) return;
        setCurrentSub(sub);
        // const isFreePlan = !sub || Number(sub.plan_id) === 1 || sub.plan_name === 'Free';
        const isFreePlan = !sub || Number(sub.plan_id) === 1 || sub.plan_name === 'Free' || sub.plan_name === 'Demo';
        if (isFreePlan) {

          const roleName = getRoleName(user?.role).toLowerCase();
          const mappedKey = roleName === 'institution' ? 'institution'
            : roleName === 'publisher' ? 'publisher'
              : roleName === 'researcher' ? 'individual'
                : null;
          const matchedRole = mappedKey ? ROLES.find(r => r.key === mappedKey) : null;
          if (matchedRole) {
            setSelectedRole(matchedRole);
            setStep('plan');
          } else {
            setStep('role');
          }
          return;
        }
        const cycleMap: Record<string, string> = { monthly: 'Monthly', annual: 'Annual', lifetime: 'Annual' };
        const primaryCycle = cycleMap[String(sub?.billing_cycle || '').toLowerCase()] || 'Annual';
        const fallbackCycle = primaryCycle === 'Annual' ? 'Monthly' : 'Annual';
        const c = await fetch(`${API_URL}/plans/detect-currency/`).then(r => r.json()).catch(() => null);
        const code = c?.code === 'INR' ? 'INR' : 'USD';

        let found = false;
        for (const cycle of [primaryCycle, fallbackCycle]) {
          for (const role of ROLES) {
            const data = await fetch(`${API_URL}/plans/?type=${role.planType}&billing_cycle=${cycle}&currency=${code}`)
              .then(r => r.json()).catch(() => null);
            if (!data) continue;
            const match = (data.plans || []).some((p: Plan) => String(p.plan_id) === String(sub?.plan_id));
            if (match) {
              if (cancelled) return;
              found = true;
              setSelectedRole(role);
              setCurrency(data.currency || null);
              const currentRank = Number(sub?.amount ?? 0);
              let upgradeList = (data.plans || []).filter((p: Plan) =>
                String(p.plan_id) !== String(sub?.plan_id) && planRank(p) > currentRank
              );
              // Same empty-list fallback as loadPlansForRole: if this cycle
              // has no higher-tier plans, check the other cycle before
              // concluding there's nothing to upgrade to.
              if (upgradeList.length === 0) {
                const other = cycle === primaryCycle ? fallbackCycle : primaryCycle;
                const otherData = await fetch(`${API_URL}/plans/?type=${role.planType}&billing_cycle=${other}&currency=${code}`)
                  .then(r => r.json()).catch(() => null);
                const otherList = (otherData?.plans || []).filter((p: Plan) =>
                  String(p.plan_id) !== String(sub?.plan_id) && planRank(p) > currentRank
                );
                if (otherList.length > 0) {
                  upgradeList = otherList;
                  setCurrency(otherData.currency || null);
                }
              }
              setPlans(upgradeList);
              break;
            }
          }
          if (found) break;
        }
        if (!found && !cancelled) setStep('role');
      } catch {
        // If we can't detect the subscription, fall back to showing the
        // normal role picker rather than blocking the page.
        if (!cancelled) setStep('role');
      } finally {
        if (!cancelled) { setDetectingRole(false); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [upgradeOnly]);

  useEffect(() => {
    // Skip while the upgrade-only bootstrap effect is still auto-detecting
    // the role from the current paid plan — it fetches+filters on its own.
    if (!selectedRole || (upgradeOnly && detectingRole)) return;
    loadPlansForRole(selectedRole, upgradeOnly ? currentSub : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);


  // const handleFreeStart = async () => {
  //   navigate('/dashboard', { replace: true });
  // };

  const loadRazorpay = (): Promise<boolean> => {
    return new Promise(resolve => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // const handlePaidPlan = async (plan: Plan) => {
  //   if (plan.is_contact_sales || plan.contact_sales) {
  //     navigate('/contact?inquiry=sales');
  //     return;
  //   }
  //   setPaying(true);
  //   setPayingPlanId(plan.plan_id); setError('');
  //   try {
  //     const loaded = await loadRazorpay();
  //     if (!loaded) { alert('Payment gateway failed to load. Please try again.'); return; }

  //     const order = await apiFetch('/payments/create-order/', {
  //       method: 'POST',
  //       body: JSON.stringify({ plan_id: plan.plan_id, pricing_id: plan.pricing_id }),
  //     });
  //     const options = {
  //       key: order.razorpay_key_id,
  //       amount: order.amount,
  //       currency: order.currency,
  //       name: 'Manna Plag',
  //       description: order.plan_name,
  //       image: "https://mannaplag.mannaanalytics.com/manna-plag-favicon.png",
  //       order_id: order.order_id,
  //       handler: async (response: any) => {
  //         await apiFetch('/payments/verify/', {
  //           method: 'POST',
  //           body: JSON.stringify(response),
  //         });
  //         navigate('/dashboard', { replace: true });
  //       },
  //       prefill: { email: user?.email, name: user?.full_name },
  //       theme: { color: '#003c6c' },
  //     };
  //     const rzp = new window.Razorpay(options);
  //     rzp.open();
  //   } catch (e: any) {
  //     setError(e.message)
  //     console.error(e);
  //   } finally {
  //     setPaying(false);
  //     setPayingPlanId(null);
  //   }
  // };
  const handlePaidPlan = async (plan: Plan) => {
    if (plan.is_contact_sales || plan.contact_sales) {
      navigate('/contact?inquiry=sales');
      return;
    }
    // if (plan.is_contact_sales || plan.contact_sales) {
    //   setContactPlan(plan);
    //   setShowContactSales(true);
    //   return;
    // }
    setPaying(true);
    setPayingPlanId(plan.plan_id); setError('');
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Payment system failed to load.');

      const token = localStorage.getItem('access_token');
      const res = await fetch(`${BASE_URL}/payments/create-order/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          pricing_id: plan.pricing_id,
          currency_id: currency?.currency_id || 1,
        }),
      });
      const orderData = await res.json();
      const currentUser = TokenStorage.getUser();
      TokenStorage.setUser({
        ...currentUser,
        role: orderData.role,
      });
      if (!res.ok) throw new Error(orderData.error || 'Order creation failed.');

      if (!orderData.razorpay_key_id) {
        if (plan.billing_cycle === "Free" || plan.billing_cycle === "Monthly") {
          // Explicitly hit /auth/me/ before landing on the dashboard so the
          // user's role/plan info is confirmed fresh from the server.
          await refreshUser();
          navigate('/dashboard');
          return;
        }

        throw new Error('Payment could not start — missing Razorpay key from server.');
      }

      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Manna Plag',
        description: orderData.plan_name,
        image: "https://mannaplag.mannaanalytics.com/manna-plag-favicon.png",
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: { color: '#003c6c' },

        handler: async (response: any) => {
          try {
            const vRes = await fetch(`${BASE_URL}/payments/verify/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                subscription_id: orderData.subscription_id,
              }),
            });
            const vData = await vRes.json();
            if (!vRes.ok) throw new Error(vData.error || 'Verification failed.');
            rzp.close();
            await refreshUser();
            setTimeout(() => {
              navigate('/dashboard?payment=success&plan=' + encodeURIComponent(vData.plan_name || ''), {
                replace: true,
              });
            }, 150);
          } catch (e: any) {
            setError(e.message || 'Payment verified but activation failed. Contact support.');
            setPaying(false);
            setPayingPlanId(null);
          }
        },
        modal: { ondismiss: () => { setPaying(false); setPayingPlanId(null); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp: any) => {
        setError(`Payment failed: ${resp?.error?.description || 'please try again.'}`);
        setPaying(false);
        setPayingPlanId(null);
      });
      rzp.open();
    } catch (e: any) {
      setError(e.message || 'Payment failed. Please try again.');
      setPayingPlanId(null);
      setPaying(false);
    }
  };

  return (
<div className="page">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Back
          </button>
          <img src="/manna-plag-logo-horizontal.png?v=2" alt="Manna Plag" className="logo"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
        {/* <button onClick={() => navigate('/dashboard', { replace: true })}
          style={{ fontSize: 12, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>
          Skip for now →
        </button> */}
      </div>
      {error && (
        <div className="error-box">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      )}
      <div className="container">
 
        {/* Step indicator — only relevant for the fresh-signup role→plan flow */}
        {!upgradeOnly && (
          <div className="step-indicator">
            <div className="step-circle step-circle--active">1</div>
            <span className={`step-label ${step === 'role' ? 'step-label--active' : 'step-label--inactive'}`}>Choose your role</span>
            <div className={`step-divider ${step === 'plan' ? 'step-divider--active' : 'step-divider--inactive'}`} />
            <div className={`step-circle ${step === 'plan' ? 'step-circle--active' : 'step-circle--inactive'}`}>2</div>
            <span className={`step-label ${step === 'plan' ? 'step-label--active' : 'step-label--inactive'}`}>Choose your plan</span>
          </div>
        )}
 
        {step === 'role' && (
          <>
            <div className="role-heading">
              <h1 className="role-heading__title">
                {upgradeOnly ? "What type of account is this?" : 'Welcome to Manna Plag'}
              </h1>
              <p className="role-heading__subtitle">
                {upgradeOnly
                  ? "We need this to show you the right upgrade plans."
                  : 'Tell us who you are so we can show you the right plans.'}
              </p>
            </div>
            <div className="role-grid">
              {ROLES.map(role => (
                <button key={role.key} onClick={() => { setSelectedRole(role); setStep('plan'); }}
                  className="role-card"
                >
                  <div className="role-card__icon">
                    {role.icon}
                  </div>
                  <p className="role-card__label">{role.label}</p>
                  <p className="role-card__desc">{role.desc}</p>
                </button>
              ))}
            </div>
          </>
        )}
 
        {step === 'plan' && (selectedRole || upgradeOnly) && (
          <>
            <div className="plan-header-row">
              {/* {!upgradeOnly && (
                <button onClick={() => setStep('role')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>
                  ← Back
                </button>
              )} */}
              <div>
                <h1 className="plan-header-row__title">
                  {upgradeOnly ? 'Upgrade your plan' : 'Choose your plan'}
                </h1>
                <p className="plan-header-row__subtitle">
                  {upgradeOnly
                    ? `Showing plans above your current ${currentSub?.plan_name || 'plan'}. Downgrades aren't available here.`
                    : ` ${selectedRole?.label} · Annual billing`}
                </p>
              </div>
            </div>
 
            {(loading || detectingRole) ? (
              <div className="loading-state">Loading plans...</div>
            ) : upgradeOnly && plans.length === 0 ? (
              <div className="top-plan-notice">
                <p className="top-plan-notice__title">You're already on our top plan</p>
                <p className="top-plan-notice__desc">There's no higher tier available to upgrade to right now.</p>
                <Link to="/contact?inquiry=sales" className="top-plan-notice__link">Contact sales for a custom plan →</Link>
              </div>
            ) : (
              <div className="plan-grid">
                {plans.map(plan => {
                  const isFree = plan.price === 0 && !plan.is_contact_sales;
                  const isContact = plan.is_contact_sales || plan.contact_sales;
                  const isPro = plan.name === 'Pro';
                  const sym = plan.currency_symbol || currency?.symbol || '₹';
 
                  return (
                    <div key={plan.plan_id} className={`plan-card ${isPro ? 'plan-card--pro' : ''}`}>
                      {isPro && (
                        <div className="plan-card__badge">
                          Most Popular
                        </div>
                      )}
                      <div className="plan-card__body">
                        <p className="plan-card__name">{plan.name}</p>
                        {isFree ? (
                          <>
                            <p className="plan-card__price">
                              {sym}0<span className="plan-card__price-suffix">/mo</span>
                            </p>
                            <p className="plan-card__gst-note">+ GST (18%) = {sym}0 total</p>
                          </>
                        ) : isContact ? (
                          <p className="plan-card__price-custom">Custom</p>
                        ) : (
                          <>
                            <p className="plan-card__price">
                              {sym}{plan.discounted_amount.toLocaleString('en-IN')}
                              <span className="plan-card__price-suffix">/yr</span>
                            </p>
                            <p className="plan-card__gst-note">
                              + GST (18%) = {sym}{formatAmount(plan.total_amount ?? 0)} total
                            </p>
                          </>
                        )}
                        <p className="plan-card__billing-cycle">{isFree ? 'Forever free' : isContact ? 'Contact us' : plan.billing_cycle}</p>
 
                        <div className="plan-card__papers-badge">
                          {plan.max_papers} papers
                        </div>
 
                        <div className="plan-card__features">
                          {[
                            isFree ? '1 check per month' : `${plan.max_papers} checks/year`,
                            plan.max_users === 1 ? '1 user' : `Up to ${plan.max_users} users`,
                            plan.ai_detection && 'AI content detection',
                            plan.full_report && 'Full similarity report',
                            plan.rest_api && 'REST API access',
                            'Email support',
                          ].filter(Boolean).map((f, i) => (
                            <div key={i} className="plan-card__feature">
                              <span className="plan-card__feature-check">✓</span> {f}
                            </div>
                          ))}
                        </div>
                      </div>
 
                      <div className="plan-card__footer">
                        <button
                          onClick={() => /* isFree ? handleFreeStart() : */ handlePaidPlan(plan)}
                          disabled={paying}
                          className="plan-card__cta"
                          style={{ cursor: payingPlanId === plan.plan_id ? 'not-allowed' : 'pointer' }}
                        >
                          {isContact
                            ? 'Contact sales'
                            : isFree
                              ? 'Get started free'
                              : payingPlanId === plan.plan_id
                                ? 'Processing...'
                                : `Start with ${plan.name}`}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
 
            <p className="footer-note">
              All plans include a free trial. Cancel anytime.{' '}
              <Link to="/pricing" className="footer-note__link">View full pricing details</Link>
            </p>
          </>
        )}
      </div>
 
    </div>
  );
}